import React, { useRef, useEffect, useState, memo } from 'react';
import axios from 'axios';
import styles from './FilesItem.module.css';
import playIcon from '../icons/play.png';
import pauseIcon from '../icons/pause.png';

const FilesItem = ({
  id,
  playId,
  orderId,
  coverImage,
  title,
  genre,
  bpm,
  keySignature,
  moodTags,
  purchasedWav,
  purchasedKit,
  licenseGenerated,
  licenseUrl,
  downloadUrl,
  setPurchasedFiles,
  audioFile,
  isPlaying,
  onPlay,
  onTagClick,
  selectedTags,
  isAuthenticated,
  artistSticker,
}) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [metadataInitialized, setMetadataInitialized] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [fullName, setFullName] = useState("");
  const [licenseGenerating, setLicenseGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [localLicenseGenerated, setLocalLicenseGenerated] = useState(licenseGenerated);
  const [localLicenseUrl, setLocalLicenseUrl] = useState(licenseUrl);
  const [isLicenseAvailable, setIsLicenseAvailable] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLocalLicenseGenerated(licenseGenerated);
    setLocalLicenseUrl(licenseUrl);
  }, [licenseGenerated, licenseUrl]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (localLicenseGenerated && localLicenseUrl) {
      const token = localStorage.getItem('authToken');
      const checkLicenseAvailability = async () => {
        try {
          await axios.head(localLicenseUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          setIsLicenseAvailable(true);
        } catch {
          setIsLicenseAvailable(false);
        }
      };
      checkLicenseAvailability();
    }
  }, [localLicenseGenerated, localLicenseUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => {
      onPlay(null);
      setIsAudioPlaying(false);
    };
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    if (isPlaying) {
     if (!audio.paused) return;
      audio.play()
        .then(() => {
	 setIsAudioPlaying(true);
          if ('mediaSession' in navigator && !metadataInitialized) {
            navigator.mediaSession.metadata = new MediaMetadata({
              title: title,
              artist: 'Quality Performance Core',
              album: 'qpCore Beats',
              artwork: [
                { src: coverImage, sizes: '512x512', type: 'image/png' }
              ]
            });

            navigator.mediaSession.setActionHandler('play', () => {
              onPlay(playId);
            });
            navigator.mediaSession.setActionHandler('pause', () => {
              onPlay(null);
            });
           setMetadataInitialized(true);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
	  console.error('Audio play failed:', error);
         }
	  if (!isPlaying) {
	  setIsAudioPlaying(false);
         }
        });
    } else {
	if (audio.paused) return; 
        audio.pause();
        setIsAudioPlaying(false);

      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
      }
    }
  }, [isPlaying, isAudioPlaying, metadataInitialized, title, coverImage, onPlay, playId]);

  useEffect(() => {
    const audio = audioRef.current;
    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration);
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateTime);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateTime);
    };
  }, []);

useEffect(() => {
  const audio = audioRef.current;

  const syncState = () => {
    const isCurrentlyPlaying = !audio.paused && !audio.ended;
    if (isCurrentlyPlaying !== isPlaying) {
      onPlay(isCurrentlyPlaying ? playId : null);
    }
  };

  audio.addEventListener('play', syncState);
  audio.addEventListener('pause', syncState);
  audio.addEventListener('ended', syncState);

  return () => {
    audio.removeEventListener('play', syncState);
    audio.removeEventListener('pause', syncState);
    audio.removeEventListener('ended', syncState);
  };
}, [isPlaying, onPlay, playId]);

  const handleDesktopWaveformClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    audioRef.current.currentTime = ratio * duration;
  };

  const handleMobileCoverClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = e.clientX - centerX;
    const y = e.clientY - centerY;
    const angle = Math.atan2(x, -y); 
    let deg = angle * (180 / Math.PI);
    if (deg < 0) deg += 360;
    const ratio = deg / 360;
    audioRef.current.currentTime = ratio * duration;
  };

  const loadAndPlayAudio = () => {
    if (!audioRef.current.src) {
      audioRef.current.src = audioFile;
      audioRef.current.load();        
    }
    onPlay(playId);
  };

  const handleGenerateLicense = async () => {
    if (!fullName.trim()) {
      setMessage("Hey, drop your full name here!");
      setMessageType("error");
      return;
    }

    setLicenseGenerating(true);
    try {
      const token = localStorage.getItem('authToken');

      const response = await axios.post('/api/licenses/generate-license', {
	orderId: orderId,
        fullName,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.licenseUrl) {
        setPurchasedFiles((prevFiles) =>
          prevFiles.map((file) =>
            file.id === id
              ? { ...file, licenseGenerated: true, licenseUrl: response.data.licenseUrl }
              : file
          )
        );
    
        setLocalLicenseGenerated(true);
        setLocalLicenseUrl(response.data.licenseUrl);

	setMessage("License successfully generated!");
        setMessageType("success");

        const checkLicenseAvailability = async () => {
          try {
            await axios.head(response.data.licenseUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            setIsLicenseAvailable(true);
          } catch {
            setIsLicenseAvailable(false);
          }
        };
        checkLicenseAvailability();

      } else {
        setMessage("License generation failed. Try again later.");
        setMessageType("error");
    }
  } catch {
      setMessage("Failed to generate license. Check your input or try again later.");
      setMessageType("error");
    } finally {
      setLicenseGenerating(false);
    }
  };

  const handleDownload = async (type) => {
    let downloadLink;

    switch (type) {
      case 'wav':
        downloadLink = purchasedWav ? downloadUrl : null;
        break;
      case 'kit':
        downloadLink = purchasedKit ? downloadUrl : null;
        break;
      case 'license':
        downloadLink = localLicenseUrl;
        break;
      default:
        return;
    }

    if (!downloadLink) {
      setMessage("Download link is not available.");
      setMessageType("error");
      return;
    }

    try {
      setDownloadProgress(0);
      const token = localStorage.getItem('authToken');

      await axios.head(downloadLink, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const response = await axios.get(downloadLink, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
	onDownloadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setDownloadProgress(percent);
       },
      });

      let filename = '';
      if (type === 'wav') {
        filename = `${title}.wav`;
      } else if (type === 'kit') {
        filename = `${title}.zip`;
      } else if (type === 'license') {
        filename = 'license.pdf';
      }

      const blob = new Blob([response.data]);
      const downloadUrlBlob = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrlBlob;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrlBlob);

    } catch {
      setMessage("Cooking up your file, check back in a few!");
      setMessageType("error");
    } finally {
    setDownloadProgress(null);
   }
  };

  return (
    <div className={styles.filesItem}>
     <audio ref={audioRef} preload="metadata" />
      <button onClick={loadAndPlayAudio} className={styles.playButton}>
        <img src={isPlaying ? pauseIcon : playIcon} alt="Play/Pause" className={styles.playButtonImage}/>
      </button>
	
      {isMobile ? (
        <div
          className={styles.coverContainer}
          style={{ '--progress': `${(currentTime / duration) * 360}deg` }}
          onClick={handleMobileCoverClick}
        >
          <img src={coverImage} alt={`${title} cover`} className={styles.filesItemCoverMobile} loading="lazy" />
        </div>
      ) : (
      <div className={styles.filesCover}>
        <img src={coverImage} alt={`${title} cover`} className={styles.filesItemCover} loading="lazy" />
	{artistSticker && (
          <img
            src={artistSticker}
            alt="Artist Sticker"
            className={`${styles.artistSticker} ${styles.artistStickerDesktop}`}
            onClick={() => onTagClick(artistSticker)}
          />
        )}
      </div>
      )}

      <div className={styles.filesInfo}>
        <h3 className={styles.filesTitle}>{title}</h3>
        <p className={styles.filesGenre}>{genre}</p>
        <div className={styles.filesDetails}>
          <span className={styles.filesBpm}>{bpm} BPM</span>
          <span className={styles.filesKey}>Key: {keySignature}</span>
        </div>
        <div className={styles.filesTags}>
          {moodTags.map((tag, index) => (
	   <button
              key={index}
	      className={`${styles.filesTag} ${selectedTags.includes(tag) ? styles.activefilesTag : ''}`} 
              onClick={() => onTagClick(tag)} 
            >
              #{tag}
            </button>	  
          ))}
        </div>
      </div>

      {!isMobile && (
        <div
          className={styles.waveformContainer}
          onClick={handleDesktopWaveformClick}
        >
          <div
            className={styles.waveformProgress}
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      )}

      <div className={styles.filesActions}>
      {!localLicenseGenerated && (
       <input
        type="text"
        placeholder="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        className={styles.inputField}
       />
      )}

      {isMobile && artistSticker && (
      <img
        src={artistSticker}
        alt="Artist Sticker"
        className={`${styles.artistSticker} ${styles.artistStickerMobile}`}
        onClick={() => onTagClick(artistSticker)}
      />
    )}

      <div className={styles.filesPrice}>
        {purchasedWav && (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('wav')}
          >
            Download WAV
          </button>
        )}
        {purchasedKit && (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('kit')}
          >
            Download KIT
          </button>
        )}
        {localLicenseGenerated && localLicenseUrl ? (
          <button
            className={styles.downloadButton}
            onClick={() => handleDownload('license')}
	    disabled={!isLicenseAvailable}
          >
           {isLicenseAvailable ? 'Download License' : 'Prepared...'} 
          </button>
        ) : (
          <button
            className={styles.generateButton}
	    onClick={handleGenerateLicense}
            disabled={licenseGenerating}
          >
            {licenseGenerating ? "Generating..." : "Generate License"}
          </button>
        )}
      </div>
    </div>

	{message && (
          <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>{message}</div>
        </div>
      )}
      {downloadProgress !== null && (
      <div className={styles.progressOverlay}>
        <div className={styles.progressText}>{downloadProgress}%</div>
      </div>
    )}
   </div>
  );
};

export default memo(FilesItem);

